<template>
  <PerfectScrollbar>
    <main>
      <div
        class="c-app flex-row align-items-center"
        :class="{ 'c-dark-theme': $store.state.layout.darkMode }"
      >
        <CContainer>
          <SecurlyMarketingBanner
            hide-banner
            heading="Looking into Pass for the first time?"
            link-text="Start Here"
            link="https://www.securly.com/pass"
          />
          <CRow class="justify-content-center">
            <CCol md="12" lg="6" xl="6">
              <CCardGroup>
                <CCard class="p-1">
                  <CCardBody>
                    <CForm v-if="!redirectLoading" @submit.prevent="login">
                      <div class="d-flex justify-content-center mb-4">
                        <img
                          class="w-50"
                          src="@/assets/images/securlypass-color-logo.png"
                          alt="Securly Logo"
                        />
                      </div>
                      <h1 class="sr-only">Log in to your account</h1>
                      <p class="text-muted mt-4">
                        If your school uses a third party service for
                        authentication (such as Google, Clever, Microsoft,
                        ClassLink) please log in by choosing the appropriate
                        button below.
                      </p>
                      <div class="pb-2 base-blue-text">
                        If you have trouble logging in, please delete your
                        browser cache and cookies and try again. Here are
                        instructions for how to do this:
                        <a
                          class="base-blue-text underline"
                          href="https://docs.google.com/document/d/e/2PACX-1vRpzc_y-8QZhnaJBikwrswMHE6EKAoPQqHXGvEg4mQ7oowjI80W53fYc7z29nFz3Q/pub#id.gjdgxs"
                          target="_blank"
                          aria-label="Instructions for clearing browser cache and cookies"
                        >
                          Instructions
                          <span class="sr-only"> - New Window</span>
                        </a>
                      </div>
                      <CRow>
                        <CCol md="12" class="text-center mb-3 px-0 px-md-2">
                          <div class="d-flex justify-content-center">
                            <div class="ext-login-container">
                              <div
                                class="ext-login-btn"
                                @click="loginGoogle('web')"
                                @keydown.enter="loginGoogle('web')"
                                @keydown.space.prevent="loginGoogle('web')"
                                role="button"
                                tabindex="0"
                                aria-label="Login with Google"
                              >
                                <span class="flaticon-google">
                                  <span class="path1" aria-hidden="true" />
                                  <span class="path2" aria-hidden="true" />
                                  <span class="path3" aria-hidden="true" />
                                  <span class="path4" aria-hidden="true" />
                                </span>
                              </div>
                              <p>Google</p>
                            </div>
                            <div class="ext-login-container">
                              <div
                                class="ext-login-btn"
                                @click="loginClever('web')"
                                @keydown.enter="loginClever('web')"
                                @keydown.space.prevent="loginClever('web')"
                                role="button"
                                tabindex="0"
                                aria-label="Login with Clever"
                              >
                                <span class="flaticon-clever" />
                              </div>
                              <p>Clever</p>
                            </div>
                            <div class="ext-login-container">
                              <div
                                class="ext-login-btn"
                                @click="loginMS('web')"
                                @keydown.enter="loginMS('web')"
                                @keydown.space.prevent="loginMS('web')"
                                role="button"
                                tabindex="0"
                                aria-label="Login with Microsoft"
                              >
                                <span class="flaticon-microsoft">
                                  <span class="path1" aria-hidden="true" />
                                  <span class="path2" aria-hidden="true" />
                                  <span class="path3" aria-hidden="true" />
                                  <span class="path4" aria-hidden="true" />
                                  <span class="path5" aria-hidden="true" />
                                  <span class="path6" aria-hidden="true" />
                                  <span class="path7" aria-hidden="true" />
                                </span>
                              </div>
                              <p>Microsoft</p>
                            </div>
                            <div class="ext-login-container">
                              <div
                                class="ext-login-btn"
                                @click="loginClassLink('web')"
                                @keydown.enter="loginClassLink('web')"
                                @keydown.space.prevent="loginClassLink('web')"
                                role="button"
                                tabindex="0"
                                aria-label="Login with ClassLink"
                              >
                                <span class="flaticon-sso" />
                              </div>
                              <p>ClassLink</p>
                            </div>
                          </div>
                        </CCol>
                      </CRow>
                      <CInputGroup
                        class="login-input-group"
                        :class="{
                          'is-invalid':
                            v$.user.$error && v$.user.email.required.$invalid
                        }"
                      >
                        <CInputGroupText
                          class="input-group-prepend"
                          aria-hidden="true"
                        >
                          <CIcon icon="cil-user" aria-label="User Icon" />
                        </CInputGroupText>
                        <CFormInput
                          id="login-email-input"
                          :class="{
                            'is-invalid':
                              v$.user.$error && v$.user.email.required.$invalid
                          }"
                          class="mb-0"
                          placeholder="Email"
                          autocomplete="email"
                          aria-label="Email"
                          aria-required="true"
                          :aria-invalid="
                            v$.user.$error && v$.user.email.required.$invalid
                          "
                          :aria-describedby="
                            v$.user.$error && v$.user.email.required.$invalid
                              ? 'login-email-error'
                              : undefined
                          "
                          data-test-id="login-email"
                          :is-valid="checkIfValid('email')"
                          v-model="v$.user.email.$model"
                          @keyup="checkForEnter"
                        >
                        </CFormInput>
                      </CInputGroup>
                      <div
                        id="login-email-error"
                        data-test-id="login-username-error"
                        v-if="v$.user.$error && v$.user.email.required.$invalid"
                        class="invalid-feedback"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        Email is required.
                      </div>
                      <CInputGroup
                        class="mt-3 mb-0 login-input-group"
                        :class="{
                          'is-invalid':
                            v$.user.$error && v$.user.password.required.$invalid
                        }"
                      >
                        <CInputGroupText
                          class="input-group-prepend"
                          aria-hidden="true"
                        >
                          <CIcon
                            icon="cil-lock-locked"
                            aria-label="Password Icon"
                          />
                        </CInputGroupText>
                        <CFormInput
                          :class="{
                            'is-invalid':
                              v$.user.$error &&
                              v$.user.password.required.$invalid
                          }"
                          placeholder="Password"
                          type="password"
                          data-test-id="login-password"
                          autocomplete="current-password"
                          aria-label="Password"
                          aria-required="true"
                          :aria-invalid="
                            v$.user.$error && v$.user.password.required.$invalid
                          "
                          :aria-describedby="
                            v$.user.$error && v$.user.password.required.$invalid
                              ? 'login-password-error'
                              : undefined
                          "
                          :is-valid="checkIfValid('password')"
                          v-model="v$.user.password.$model"
                          @keyup="checkForEnter"
                        >
                        </CFormInput>
                      </CInputGroup>
                      <div
                        id="login-password-error"
                        data-test-id="login-password-error"
                        v-if="
                          v$.user.$error && v$.user.password.required.$invalid
                        "
                        class="invalid-feedback"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        Password is required.
                      </div>
                      <div
                        class="sr-only"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        {{ liveStatusMessage }}
                      </div>

                      <CAlert
                        data-test-id="login-error"
                        v-if="error"
                        class="mt-3"
                        color="danger"
                        :show="true"
                        role="alert"
                        aria-live="assertive"
                        aria-atomic="true"
                      >
                        {{ formattedLoginError }}
                      </CAlert>
                      <CAlert
                        v-if="unverifiedEmail"
                        class="mt-3 d-block"
                        color="danger"
                        :show="true"
                        role="alert"
                        aria-live="assertive"
                        aria-atomic="true"
                      >
                        This email has not been verified. Please check your
                        email to complete verification or
                        <span
                          class="cursor-pointer base-blue-text underline"
                          @click="navigateToVerifyEmail"
                        >
                          click here
                        </span>
                        to enter your verification code.
                      </CAlert>
                      <div class="d-flex justify-content-end">
                        <CButton
                          data-test-id="login-forgot-password-btn"
                          color="link"
                          @click="$router.push('/forgotten-password')"
                          aria-label="Forgot password? Click to reset your password"
                        >
                          Forgot password?
                        </CButton>
                      </div>
                      <CRow class="mt-3">
                        <CCol md="12" class="text-center">
                          <CButton
                            type="submit"
                            class="me-2 btn bg-gradient-blue text-white"
                            data-test-id="login-submit"
                            aria-label="Login to Securly Pass"
                            @click="login"
                          >
                            Login
                          </CButton>
                        </CCol>
                      </CRow>
                    </CForm>
                    <CCol md="12" class="text-center mb-3">
                      <CSpinner
                        v-if="isLoading && !goToApp && !goToVisitorApp"
                        color="primary"
                      />
                    </CCol>
                    <div
                      v-if="goToApp || goToVisitorApp"
                      class="d-flex flex-column flex-wrap align-items-center gap-4"
                    >
                      <div class="base-blue-text">
                        If you are logging into the Securly Pass App please
                        click here
                      </div>
                      <a
                        class="btn bg-gradient-blue text-white"
                        :href="goToApp"
                      >
                        Go To PASS App
                      </a>
                      <div class="base-blue-text">
                        If you are logging into the Securly Visitor App please
                        click here
                      </div>
                      <a
                        class="btn bg-gradient-blue text-white"
                        :href="goToVisitorApp"
                      >
                        Go To Visitor App
                      </a>
                    </div>
                  </CCardBody>
                </CCard>
              </CCardGroup>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    </main>
  </PerfectScrollbar>
</template>

<script>
import SecurlyMarketingBanner from "@/v3components/SecurlyMarketingBanner.vue"
import useVuelidate from "@vuelidate/core"
import { required } from "@vuelidate/validators"
import tokenService from "@/helpers/tokenService.js"
import localforage from "localforage"
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import { stripPerfectScrollbarThumbTabindex } from "@/helpers/stripPerfectScrollbarThumbTabindex.js"
/* eslint-disable */
const randomstring = require("randomstring")
const CryptoJS = require("crypto-js")
import sha256 from "crypto-js/sha256"
const base64url = require("base64url")
/* eslint-disable */

export default {
  name: "Login",
  components: {
    SecurlyMarketingBanner
  },
  setup() {
    return { v$: useVuelidate() }
  },
  data: () => ({
    redirectLoading: false,
    isLoading: false,
    user: {
      email: "",
      password: ""
    },
    error: null,
    goToApp: null,
    goToVisitorApp: null,
    goToWeb: null,
    unverifiedEmail: false,
    previousDocumentTitle: "",
    previousMetaDescription: "",
    robotsMetaWasInserted: false,
    robotsMetaWasModified: false,
    savedRobotsMetaContent: ""
  }),
  validations() {
    return {
      user: {
        email: {
          required
        },
        password: {
          required
        }
      }
    }
  },
  computed: {
    isValid() {
      return !this.v$.user.$invalid
    },
    formattedLoginError() {
      if (!this.error) {
        return ""
      }
      return this.error === "Invalid user credentials."
        ? "Invalid email or password. Use Forgot password or contact your school admin."
        : this.error
    },
    liveStatusMessage() {
      if (this.isLoading) {
        return "Logging in. Please wait."
      }

      if (this.unverifiedEmail) {
        return "This email has not been verified. Please verify your email."
      }

      if (this.error) {
        return this.formattedLoginError
      }

      const hasValidationErrors = this.v$?.user?.$error
      if (!hasValidationErrors) {
        return ""
      }

      const validationMessages = []
      if (this.v$?.user?.email?.required?.$invalid) {
        validationMessages.push("Email is required.")
      }
      if (this.v$?.user?.password?.required?.$invalid) {
        validationMessages.push("Password is required.")
      }

      return validationMessages.join(" ")
    }
  },
  mounted() {
    this.setSeo()

    localforage.clear()

    if (this.$route.name !== "login") {
      this.redirectLoginToSource(this.$route.name)
    }

    if (this.$route.query && this.$route.query.mobile_redirect) {
      this.isLoading = true
      this.redirectLoading = true
      this.handleMobileRedirect(this.$route.query.mobile_redirect)
    }

    if (this.$route.query && this.$route.query.code) {
      if (this.$route.params.source === "mobile") {
        this.redirectLoading = true
      }
      this.isLoading = true

      const state =
        this.$route.query.state ??
        this.getProviderIDByName(this.$route.params.type)
      const redirectUrl = this.getRedirectUri(
        this.$route.params.source,
        this.$route.params.type
      )

      const params = {
        ...this.$route.query,
        redirectUri: redirectUrl,
        codeVerifier: sessionStorage.getItem("code_verifier")
          ? sessionStorage.getItem("code_verifier")
          : null,
        state: state,
        isMobile: this.$route.params.source === "mobile",
        storedVerifier: state == "20" ? this.getCodeVerifier(false) : null,
        generatedVerifier: state == "20" ? this.getCodeVerifier(false) : null
      }
      this.$store
        .dispatch("authentication/externalLogin", params)
        .then((response) => {
          const data = response.data
          if (this.$route.params.source === "mobile") {
            this.afterLoginMobile(data.access_token)
          } else {
            this.afterLoginWeb(data)
          }
          this.isLoading = false
        })
        .catch((err) => {
          if (this.$route.params.source === "mobile") {
            this.afterLoginMobile(null, err.message)
          } else {
            this.isLoading = false
            this.error = err.message
          }
        })
    }

    if (this.$route.query && this.$route.query.token) {
      const token = this.$route.query.token
      this.$store.dispatch("authentication/saveToken", token)
      this.$store.dispatch("authentication/getUserDetails").then((user) => {
        if (user && user.role_id == 1) {
          window.location.href = "/passes/create"
        } else if (user && user.role_id == 2) {
          window.location.href = advanceReportingConstants.PATHS.REPORT
        } else {
          window.location.href = "/dashboard"
        }
      })
      window.location.href = window.location.origin + "/"
    }

    this.$nextTick(() => {
      if (this?.$el) {
        stripPerfectScrollbarThumbTabindex(this.$el)
      }
    })

    if (typeof MutationObserver !== "undefined" && this?.$el) {
      this._psThumbObserver = new MutationObserver(() => {
        if (this?.$el) {
          stripPerfectScrollbarThumbTabindex(this.$el)
        }
      })
      this._psThumbObserver.observe(this.$el, {
        subtree: true,
        childList: true
      })
    }
  },
  beforeUnmount() {
    this._psThumbObserver?.disconnect()
    this._psThumbObserver = null
  },
  beforeUnmount() {
    this.unsetSeo()
  },
  methods: {
    setSeo() {
      this.previousDocumentTitle = document.title

      let metaDescriptionEl = document.getElementById(
        "pass-login-seo-meta-description"
      )
      if (!metaDescriptionEl) {
        metaDescriptionEl = document.querySelector('meta[name="description"]')
        if (metaDescriptionEl) {
          metaDescriptionEl.id = "pass-login-seo-meta-description"
        }
      }
      this.previousMetaDescription =
        metaDescriptionEl?.getAttribute("content") ?? ""

      document.title = "Customer Portal Sign-In | Securly"
      if (metaDescriptionEl) {
        metaDescriptionEl.setAttribute(
          "content",
          "Log in to your Securly account to access your dashboard and manage your settings."
        )
      }

      let robotsMetaEl = document.getElementById("pass-login-seo-meta-robots")
      if (robotsMetaEl) {
        this.robotsMetaWasModified = true
        this.savedRobotsMetaContent = robotsMetaEl.getAttribute("content") ?? ""
        robotsMetaEl.setAttribute("content", "nosnippet, noarchive, nofollow")
      } else {
        const existingRobotsMeta = document.querySelector('meta[name="robots"]')
        if (existingRobotsMeta) {
          this.robotsMetaWasModified = true
          this.savedRobotsMetaContent =
            existingRobotsMeta.getAttribute("content") ?? ""
          existingRobotsMeta.id = "pass-login-seo-meta-robots"
          existingRobotsMeta.setAttribute(
            "content",
            "nosnippet, noarchive, nofollow"
          )
        } else {
          robotsMetaEl = document.createElement("meta")
          robotsMetaEl.id = "pass-login-seo-meta-robots"
          robotsMetaEl.setAttribute("name", "robots")
          robotsMetaEl.setAttribute("content", "nosnippet, noarchive, nofollow")
          document.head.appendChild(robotsMetaEl)
          this.robotsMetaWasInserted = true
        }
      }
    },
    unsetSeo() {
      document.title = this.previousDocumentTitle

      const metaDescriptionEl = document.getElementById(
        "pass-login-seo-meta-description"
      )
      if (metaDescriptionEl) {
        metaDescriptionEl.setAttribute("content", this.previousMetaDescription)
      }

      const robotsMetaEl = document.getElementById("pass-login-seo-meta-robots")
      if (this.robotsMetaWasInserted && robotsMetaEl) {
        robotsMetaEl.remove()
      } else if (this.robotsMetaWasModified && robotsMetaEl) {
        robotsMetaEl.setAttribute("content", this.savedRobotsMetaContent)
      }
    },
    checkIfValid(fieldName) {
      const field = this.v$.user[fieldName]
      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || field.$model === "")
    },
    checkForEnter(e) {
      if (e.keyCode === 13) {
        this.login()
      }
    },
    login() {
      if (this.isLoading) {
        return
      }
      this.errors = null
      this.error = null
      this.unverifiedEmail = false
      if (this.isValid) {
        this.isLoading = true
        this.$store
          .dispatch("authentication/login", this.user)
          .then((response) => {
            const data = response.data
            if (data.redirect) {
              window.location.replace(data.url)
            }
            this.afterLoginWeb(data)
          })
          .catch((err) => {
            this.isLoading = false
            if (err.message == "This user is not verified.") {
              this.unverifiedEmail = true
              return
            }
            this.error = err.message
          })
      } else {
        this.v$.$touch()
      }
    },
    afterLoginWeb(data) {
      if (data.user && data.user.redirect) {
        window.location.replace(data.user.redirect)
      } else {
        if (data.access_token) {
          console.log("at", data.access_token)
          tokenService.saveDevToken(data.access_token)
        }
        this.$store.dispatch("authentication/saveToken", data.ws.token)
        console.log("ws", data.ws.token)
        if (data.user && data.user.created_at) {
          this.$store.commit("authentication/SET_USER", data.user)
        }
        if (
          data.user.force_password_change_at == null &&
          data.user.is_manual == true
        ) {
          window.location.replace("/password/reset")
        } else {
          localStorage.setItem("showAnnouncements", true)
          if (data && data.user && data.user.role_id === 1) {
            window.location.replace("/passes/create")
          } else if (data && data.user && data.user.role_id === 2) {
            window.location.replace(advanceReportingConstants.PATHS.REPORT)
          } else {
            window.location.replace("/dashboard")
          }
        }
      }
    },
    afterLoginMobile(token, error) {
      const query = token ? "?token=" + token : "?error=" + error
      this.goToApp = import.meta.env.VITE_MOBILE_URL + "login" + query
      this.goToVisitorApp =
        import.meta.env.VITE_VISITOR_MOBILE_URL + "login" + query
      this.goToWeb = document.location.origin + "/login" + query
    },
    goToDesktop() {
      this.$store.dispatch("authentication/saveToken", token)
      window.open(this.goToWeb, "_blank")
    },
    getPKCE(random = true) {
      let code_verifier = randomstring.generate(128)

      if (!random) {
        const constant_verifier = import.meta.env.VITE_VERIFIER
        const dateString = new Date()
          .toISOString()
          .slice(0, 10)
          .replace("-", "")
          .replace("-", "")
        code_verifier = btoa(
          constant_verifier.substring(0, 50) +
            dateString +
            constant_verifier.substring(50)
        ).substring(0, 128)
      }

      sessionStorage.setItem("code_verifier", code_verifier)
      //const base64Digest = sha256(code_verifier).toString().substring(0, 43)
      //const code_challenge = base64url.fromBase64(base64Digest)
      const code_challenge = sha256(code_verifier)
        .toString(CryptoJS.enc.Base64)
        .replaceAll("+", "-")
        .replaceAll("/", "_")
        .replace(/=+$/, "")

      return code_challenge
    },
    getCodeVerifier(random = true) {
      let code_verifier = randomstring.generate(128)

      if (!random) {
        const constant_verifier = import.meta.env.VITE_VERIFIER
        const dateString = new Date()
          .toISOString()
          .slice(0, 10)
          .replace("-", "")
          .replace("-", "")
        code_verifier = btoa(
          constant_verifier.substring(0, 50) +
            dateString +
            constant_verifier.substring(50)
        ).substring(0, 128)
      }
      sessionStorage.setItem("code_verifier", code_verifier)
      return code_verifier
    },
    getRedirectUri(source = "web", type = null) {
      type = type ? `/${type}` : ""
      return `${document.location.origin}/login/${source}${type}`
    },
    loginGoogle(source = "web") {
      const redirect_uri = this.getRedirectUri(source)
      const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${
        import.meta.env.VITE_GOOGLE_CLIENT_ID
      }&redirect_uri=${redirect_uri}&prompt=consent&access_type=offline&scope=email%20profile%20openid&code_challenge=${this.getPKCE()}&code_challenge_method=S256&flowName=GeneralOAuthFlow&state=10&response_type=code`
      window.location.href = url
    },
    loginMS(source = "web") {
      const redirect_uri = this.getRedirectUri(source)
      const url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${
        import.meta.env.VITE_MICROSOFT_CLIENT_ID
      }&redirect_uri=${redirect_uri}&response_type=code&response_mode=query&scope=https%3A%2F%2Fgraph.microsoft.com%2Fuser.read%20openid%20profile%20email%20offline_access&code_challenge=${this.getPKCE(
        false
      )}&code_challenge_method=S256&state=20`
      window.location.href = url
    },
    loginClassLink(source = "web") {
      const redirect_uri = this.getRedirectUri(source, "classlink")
      const url = `https://launchpad.classlink.com/oauth2/v2/auth?scope=profile&redirect_uri=${redirect_uri}&client_id=${import.meta.env.VITE_CLASSLINK_CLIENT_ID}&response_type=code&state=101`
      window.location.href = url
    },
    loginClever(source = "web") {
      const redirect_uri = this.getRedirectUri(source, "clever")
      //https://clever.com/in/protective-information-7564
      const url = `https://clever.com/oauth/authorize?client_id=${import.meta.env.VITE_CLEVER_CLIENT_ID}&redirect_uri=${redirect_uri}&response_type=code&state=102`
      window.location.href = url
    },
    getProviderIDByName(name) {
      const providers = {
        clever: 102,
        classlink: 101
      }
      return providers[name]
    },
    handleMobileRedirect(type) {
      if (type === "google") {
        this.loginGoogle("mobile")
      } else if (type === "microsoft") {
        this.loginMS("mobile")
      } else if (type === "classlink") {
        this.loginClassLink("mobile")
      } else if (type === "clever") {
        this.loginClever("mobile")
      }
      this.isLoading = false
    },
    redirectLoginToSource(source) {
      switch (source) {
        case "glogin":
          this.loginGoogle()
          break

        case "ologin":
          this.loginMS()
          break

        case "clogin":
          this.loginClever()
          break

        case "classlink":
          this.loginClassLink()
          break

        default:
          break
      }
    },
    navigateToVerifyEmail() {
      this.$router.push("/sso/verifyEmail")
    }
  }
}
</script>
<style scoped lang="scss">
@import "src/assets/scss/_variables.scss";
.ext-login-container {
  .ext-login-btn {
    background-color: #e1e1e1;
    border-radius: 100%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin: 5px 10px;
    cursor: pointer;
    @media screen and (max-width: 600px) {
      margin: 5px;
      width: 42px;
      height: 42px;
    }

    &:focus-visible {
      outline: 2px solid $outline-color;
      outline-offset: $outline-offset;
    }
  }
  p {
    font-size: 12px;
  }
}

.ps {
  max-height: 100vh;
}

/* WCAG 1.4.11: forgot-password focus ring must stay clearly visible */
:deep([data-test-id="login-forgot-password-btn"]:focus),
:deep([data-test-id="login-forgot-password-btn"]:focus-visible),
:deep(.btn-link[data-test-id="login-forgot-password-btn"]:focus),
:deep(.btn-link[data-test-id="login-forgot-password-btn"]:focus-visible) {
  outline: 2px solid $outline-color !important;
  border-radius: 0.25rem;
  box-shadow: none !important;
}

/* WCAG 2.4.7: clearly visible focus on Login button */
:deep([data-test-id="login-submit"]:focus-visible) {
  outline: 2px solid $outline-color !important;
  outline-offset: $outline-offset;
  box-shadow: none !important;
}

/* Keep focus indication on the full custom input group (icon + input). */
:deep(.login-input-group:focus-within) {
  outline: 2px solid $outline-color;
  outline-offset: $outline-offset;
  border-radius: 0.25rem;
}

:deep(.login-input-group .form-control:focus),
:deep(.login-input-group .form-control:focus-visible) {
  box-shadow: none;
}
</style>
